/**
 * sqlite3mt DB-view overlay helpers
 *
 * Mechanical extraction from §96 §9.1–§9.4.
 *
 * This module implements page-count-aware delta application, pending delta
 * management, atomic DB-view transitions, slot invalidation, backfill
 * absorption, and WAL-cycle management.
 *
 * Every DB-view mutation goes through applyDeltaIntoDbOverlayAtomic()
 * to guarantee:
 * - dbPageCount is never mutated outside PAGE_TABLE_LOCK
 * - dbOverlay pruning is always coupled with dbPageCount changes
 * - slot invalidation happens for every page-hash change under PAGE_TABLE_LOCK
 * - no prefix-partial DB-view states are ever published
 */

import {
    DEFAULT_PAGE_SIZE,
    NUM_SLOTS,
    SAB1_DB_PAGE_COUNT_I32,
    SlotMetaI32,
    sab1CtrlView,
    sab1LoadSlotGen,
    sab1SlotMetaView,
    sab1StoreSlotGen,
} from './sab-layout'
import {
    acquirePageTableLock,
    acquireSlotLock,
    releasePageTableLock,
    unlockSlot,
} from './slot'
import {
    SlotState,
    type ControlWorkerState,
    type Delta,
    type FrameMax,
    type PageHash,
    type Pgno,
    type WalSnapshot,
    sameWalSalt,
} from './types'

// ── §9.1 Basic helpers ─────────────────────────────────────────

/**
 * Insert a pending delta ordered by frameMax.
 *
 * §9.1: If a delta already exists for this frameMax and differs, it is
 * internal corruption. If it is equal, the insert is a no-op.
 */
export function insertPendingDeltaOrdered(
    pendingDbDeltas: Map<FrameMax, Delta>,
    frameMax: FrameMax,
    delta: Delta
): void {
    const existing = pendingDbDeltas.get(frameMax)
    if (existing != null) {
        if (equalDelta(existing, delta)) {
            return
        }
        throw new Error('INTERNAL_CORRUPTION: conflicting delta at same frameMax')
    }

    let needsReorder = false
    for (const existingFrameMax of pendingDbDeltas.keys()) {
        if (existingFrameMax > frameMax) {
            needsReorder = true
            break
        }
    }

    pendingDbDeltas.set(frameMax, delta)
    if (!needsReorder) {
        return
    }

    const sortedEntries = Array.from(pendingDbDeltas.entries())
        .sort(([leftFrameMax], [rightFrameMax]) => leftFrameMax - rightFrameMax)
    pendingDbDeltas.clear()
    for (const [sortedFrameMax, sortedDelta] of sortedEntries) {
        pendingDbDeltas.set(sortedFrameMax, sortedDelta)
    }
}

/**
 * Apply a delta into the workspace overlay.
 *
 * §9.2: Updates page hashes and postPageCount, prunes pages above new EOF.
 */
export function applyDeltaIntoWorkspaceOverlay(
    workspaceOverlay: Map<Pgno, PageHash>,
    workspacePageCount: { value: number },
    delta: Delta
): void {
    for (const [pgno, hash] of delta.pageHashes) {
        workspaceOverlay.set(pgno, hash)
    }

    if (delta.postPageCount != null) {
        workspacePageCount.value = delta.postPageCount
        pruneOverlayAbovePageCount(workspaceOverlay, workspacePageCount.value)
    }
}

/**
 * Remove all entries from overlay whose pgno exceeds pageCount.
 * §9.2: Used after page-count-only changes and during delta application.
 */
export function pruneOverlayAbovePageCount(
    overlay: Map<Pgno, PageHash>,
    pageCount: number
): void {
    for (const pgno of overlay.keys()) {
        if (pgno > pageCount) {
            overlay.delete(pgno)
        }
    }
}

/**
 * §9.2: A delta that changes nothing is a no-op.
 */
export function deltaIsNoOp(delta: Delta): boolean {
    return delta.pageHashes.size === 0 && delta.postPageCount == null
}

/**
 * Structural delta equality (not deep blob equality).
 * Compares pageHashes maps and postPageCount.
 */
export function equalDelta(a: Delta, b: Delta): boolean {
    if (a.postPageCount !== b.postPageCount) return false
    if (a.pageHashes.size !== b.pageHashes.size) return false
    for (const [pgno, hash] of a.pageHashes) {
        if (b.pageHashes.get(pgno) !== hash) return false
    }
    return true
}

// ── §9.3 Atomic DB-view transitions ────────────────────────────

/**
 * Invalidate slots for pages that changed or moved above the new EOF.
 *
 * §9.2 / R24 / R56: Must run under PAGE_TABLE_LOCK.
 * Invalidates READY/FETCHING/ERROR slots whose pgno is affected by the delta
 * or is above the new page count.
 */
export function invalidateSlotsForDbDeltaUnderPageTableLock(
    sab1: SharedArrayBuffer,
    delta: Delta,
    targetPageCount: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        // Acquire slot lock to avoid racing with a fault handler
        acquireSlotLock(sab1, slot, pageSize)
        try {
            const st = Atomics.load(meta, SlotMetaI32.STATE)
            if (st === SlotState.EMPTY) continue

            const pg = Atomics.load(meta, SlotMetaI32.PGNO)
            if (pg > targetPageCount || delta.pageHashes.has(pg as Pgno)) {
                const newGen = sab1LoadSlotGen(sab1, slot, pageSize) + 1n
                sab1StoreSlotGen(sab1, slot, newGen, pageSize)
                Atomics.store(meta, SlotMetaI32.STATE, SlotState.EMPTY)
                Atomics.store(meta, SlotMetaI32.PGNO, 0)
                Atomics.add(meta, SlotMetaI32.WAITSEQ, 1)
                Atomics.notify(meta, SlotMetaI32.WAITSEQ, Infinity)
            }
        } finally {
            unlockSlot(sab1, slot, pageSize)
        }
    }
}

/**
 * Atomically apply a delta into the DB-view (dbOverlay + dbPageCount + SAB1).
 *
 * §9.3 / R52 / R55 / R56:
 * - Takes PAGE_TABLE_LOCK for the entire transition
 * - Updates dbOverlay with new page hashes
 * - Updates dbPageCount and prunes above new EOF
 * - Invalidates affected slots
 * - Publishes DB_PAGE_COUNT to SAB1
 */
export function applyDeltaIntoDbOverlayAtomic(
    sab1: SharedArrayBuffer,
    dbOverlay: Map<Pgno, PageHash>,
    dbPageCountRef: { value: number },
    delta: Delta,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    if (deltaIsNoOp(delta)) return

    const targetPageCount = delta.postPageCount ?? dbPageCountRef.value

    const ctrl = sab1CtrlView(sab1)
    acquirePageTableLock(sab1)

    try {
        for (const [pgno, hash] of delta.pageHashes) {
            dbOverlay.set(pgno, hash)
        }

        dbPageCountRef.value = targetPageCount
        pruneOverlayAbovePageCount(dbOverlay, targetPageCount)

        invalidateSlotsForDbDeltaUnderPageTableLock(sab1, delta, targetPageCount, pageSize)

        Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, targetPageCount)
    } finally {
        releasePageTableLock(sab1)
    }
}

/**
 * Consume all pending deltas up to hiFrameInclusive, collapsing them into
 * a single combined delta.
 *
 * §9.2 / R55: This is the foundational helper for atomic prefix consumption.
 * No intermediate states are published — the entire prefix is one delta.
 */
export function consumePendingPrefixInclusive(
    pendingDbDeltas: Map<FrameMax, Delta>,
    hiFrameInclusive: number
): Delta {
    const outPages = new Map<Pgno, PageHash>()
    let outPostPageCount: number | null = null

    const keysToDelete: FrameMax[] = []
    for (const [frameMax, delta] of pendingDbDeltas) {
        if (frameMax > hiFrameInclusive) break

        for (const [pgno, hash] of delta.pageHashes) {
            outPages.set(pgno, hash)
        }

        if (delta.postPageCount != null) {
            outPostPageCount = delta.postPageCount
            pruneOverlayAbovePageCount(outPages, outPostPageCount)
        }

        keysToDelete.push(frameMax)
    }

    for (const key of keysToDelete) {
        pendingDbDeltas.delete(key)
    }

    return {
        pageHashes: outPages,
        postPageCount: outPostPageCount,
    }
}

/**
 * Fold all pending deltas into dbOverlay atomically.
 *
 * §9.3: Used during WAL-cycle transitions, shrink/reset, and bootstrap.
 */
export function foldAllPendingIntoDbOverlay(
    sab1: SharedArrayBuffer,
    pendingDbDeltas: Map<FrameMax, Delta>,
    dbOverlay: Map<Pgno, PageHash>,
    dbPageCountRef: { value: number },
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    const combined = consumePendingPrefixInclusive(pendingDbDeltas, Infinity)
    applyDeltaIntoDbOverlayAtomic(sab1, dbOverlay, dbPageCountRef, combined, pageSize)
}

/**
 * Collect pending tail deltas in the range (loFrameMax, hiFrameMax].
 *
 * §9.2: Used by catchUpMergePlanToFrame for reconcile catch-up.
 */
export function collectPendingTailExclusive(
    pendingDbDeltas: Map<FrameMax, Delta>,
    loFrameMax: number,
    hiFrameMax: number
): Delta {
    const outPages = new Map<Pgno, PageHash>()
    let outPostPageCount: number | null = null

    for (const [frameMax, delta] of pendingDbDeltas) {
        if (frameMax <= loFrameMax) continue
        if (frameMax > hiFrameMax) break

        for (const [pgno, hash] of delta.pageHashes) {
            outPages.set(pgno, hash)
        }

        if (delta.postPageCount != null) {
            outPostPageCount = delta.postPageCount
            pruneOverlayAbovePageCount(outPages, outPostPageCount)
        }
    }

    return {
        pageHashes: outPages,
        postPageCount: outPostPageCount,
    }
}

// ── §9.3 DB-page-count-only transitions ────────────────────────

/**
 * Set DB page count and invalidate slots above EOF if needed.
 *
 * §9.3: Uses applyDeltaIntoDbOverlayAtomic for the actual transition.
 * If both in-memory and SAB already match, this is a no-op.
 */
export function setDbPageCountAndInvalidateEofIfNeeded(
    sab1: SharedArrayBuffer,
    dbOverlay: Map<Pgno, PageHash>,
    dbPageCountRef: { value: number },
    newPageCount: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    if (
        newPageCount === dbPageCountRef.value
        && Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32) === newPageCount
    ) {
        return
    }

    applyDeltaIntoDbOverlayAtomic(
        sab1,
        dbOverlay,
        dbPageCountRef,
        { pageHashes: new Map(), postPageCount: newPageCount },
        pageSize
    )
}

// ── §9.4 WAL helpers ───────────────────────────────────────────

/**
 * Track the observed WAL tip from a frame append.
 *
 * §9.4: Updates lastWalSnapshot when the WAL tip grows or a new cycle starts.
 */
export function noteObservedWalTipFromAppend(
    state: { lastWalSnapshot: WalSnapshot | null },
    salt0: number,
    salt1: number,
    frameMax: number
): void {
    const snap = state.lastWalSnapshot
    if (snap == null || !sameWalSalt(snap.salt0, snap.salt1, salt0, salt1)) {
        state.lastWalSnapshot = {
            salt0,
            salt1,
            iChange: 0,
            mxFrame: frameMax,
            nBackfill: 0,
        }
        return
    }

    if (frameMax > snap.mxFrame) {
        state.lastWalSnapshot = {
            salt0,
            salt1,
            iChange: snap.iChange,
            mxFrame: frameMax,
            nBackfill: Math.min(snap.nBackfill, frameMax),
        }
    }
}

/**
 * Accept a WAL cycle from a snapshot, folding old pending deltas if the
 * cycle changed.
 *
 * §9.4: Called during advanceDbHead() and bootstrap. Handles:
 * - First WAL cycle ever (null salt)
 * - Same cycle: no-op
 * - Different cycle or shrink: fold pending, reset integrated frame
 */
export function acceptWalCycleFromSnapshot(
    sab1: SharedArrayBuffer,
    state: ControlWorkerState,
    publishFrontierFn: () => void,
    snap: WalSnapshot,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    if (state.currentWalSalt0 == null) {
        state.currentWalSalt0 = snap.salt0
        state.currentWalSalt1 = snap.salt1
        state.integratedFrameMax = 0
        state.lastWalSnapshot = snap
        publishFrontierFn()
        return
    }

    const sameCycle = sameWalSalt(
        state.currentWalSalt0, state.currentWalSalt1,
        snap.salt0, snap.salt1
    )
    const shrinkSameSalt =
        state.lastWalSnapshot != null
        && sameWalSalt(
            state.lastWalSnapshot.salt0, state.lastWalSnapshot.salt1,
            snap.salt0, snap.salt1
        )
        && (snap.mxFrame < state.lastWalSnapshot.mxFrame
            || snap.nBackfill < state.lastWalSnapshot.nBackfill)

    if (!sameCycle || shrinkSameSalt) {
        const dbPageCountRef = { value: state.dbPageCount }
        foldAllPendingIntoDbOverlay(
            sab1,
            state.pendingDbDeltas,
            state.dbOverlay,
            dbPageCountRef,
            pageSize
        )
        state.dbPageCount = dbPageCountRef.value
        state.currentWalSalt0 = snap.salt0
        state.currentWalSalt1 = snap.salt1
        state.integratedFrameMax = 0
        publishFrontierFn()
    }

    state.lastWalSnapshot = snap
}

/**
 * Apply backfilled pending deltas up to the current nBackfill level.
 *
 * §9.4 / §9.3: Called during advanceDbHead(). Consumes pending deltas that
 * have been backfilled (integrated into MainDb by SQLite) and applies them
 * atomically to the DB-view.
 */
export function applyBackfilledPending(
    sab1: SharedArrayBuffer,
    pendingDbDeltas: Map<FrameMax, Delta>,
    dbOverlay: Map<Pgno, PageHash>,
    dbPageCountRef: { value: number },
    snap: WalSnapshot,
    integratedFrameMax: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    const effectiveBackfill = Math.min(snap.nBackfill, integratedFrameMax)
    const combined = consumePendingPrefixInclusive(pendingDbDeltas, effectiveBackfill)
    applyDeltaIntoDbOverlayAtomic(sab1, dbOverlay, dbPageCountRef, combined, pageSize)
}

// ── §5/§9 Page-count-aware workspace helpers ───────────────────

/**
 * §14.1: workspaceHasLocalDelta checks if there are unpushed local changes.
 * A page-count-only change (with empty overlay) is still dirty.
 */
export function workspaceHasLocalDelta(
    workspaceOverlay: ReadonlyMap<Pgno, PageHash>,
    workspacePageCount: number,
    workspaceBaseHeadRootPageCount: number
): boolean {
    return workspaceOverlay.size > 0
        || workspacePageCount !== workspaceBaseHeadRootPageCount
}

/**
 * Subtract a published snapshot from the live workspace state.
 *
 * §14.1: After successful push, removes published pages from overlay
 * and restores the live page count if it changed under the push.
 */
export function subtractPublishedSnapshotFromLiveWorkspaceState(
    workspaceOverlay: Map<Pgno, PageHash>,
    workspacePageCountRef: { value: number },
    snapshotOverlay: ReadonlyMap<Pgno, PageHash>,
    snapshotPageCount: number
): void {
    const livePageCountBefore = workspacePageCountRef.value

    for (const [pgno, hash] of snapshotOverlay) {
        if (workspaceOverlay.get(pgno) === hash) {
            workspaceOverlay.delete(pgno)
        }
    }

    if (livePageCountBefore !== snapshotPageCount) {
        workspacePageCountRef.value = livePageCountBefore
    } else {
        workspacePageCountRef.value = snapshotPageCount
    }

    pruneOverlayAbovePageCount(workspaceOverlay, workspacePageCountRef.value)
}

/**
 * §15.3: Resolve merged page count using the three-way rule.
 */
export function resolveMergedPageCount(
    basePageCount: number,
    localPageCount: number,
    remotePageCount: number
): number {
    if (localPageCount === remotePageCount) return localPageCount
    if (localPageCount === basePageCount) return remotePageCount
    if (remotePageCount === basePageCount) return localPageCount
    return Math.max(basePageCount, localPageCount, remotePageCount)
}
